import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconSend } from "@tabler/icons-react"
import { useMemo, useState } from "react"

interface Cycle {
  id: number
  display_name: string
  user_name: string
}

interface Review {
  id: number
  reviewer_type: string
  reviewer_name: string
  status: string
}

interface Question {
  id: number
  question_text: string
  question_type: string
  category: string
  display_category: string
  options: string[]
  weight: number
}

interface AnswerData {
  id: number | null
  review_question_template_id: number
  answer_value: string
}

interface Props {
  [key: string]: unknown
  cycle: Cycle
  review: Review
  questions: Question[]
  review_answers: AnswerData[]
}

export default function PerformanceReviewEdit() {
  const { cycle, review, questions, review_answers } = usePage<Props>().props

  const [answers, setAnswers] = useState<Record<number, AnswerData>>(() => {
    const map: Record<number, AnswerData> = {}
    review_answers.forEach((a) => {
      map[a.review_question_template_id] = { ...a }
    })
    return map
  })

  const [processing, setProcessing] = useState(false)

  // Group questions by category
  const groupedQuestions = useMemo(() => {
    const groups: Record<string, Question[]> = {}
    questions.forEach((q) => {
      const cat = q.display_category
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(q)
    })
    return groups
  }, [questions])

  function setAnswer(questionId: number, value: string) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        review_question_template_id: questionId,
        answer_value: value,
      },
    }))
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    submitForm()
  }

  function handleSubmitReview() {
    if (confirm("Submit this review? This action cannot be undone.")) {
      submitForm(() => {
        router.post(`/performance_review_cycles/${cycle.id}/performance_reviews/${review.id}/submit_review`)
      })
    }
  }

  function submitForm(onSuccess?: () => void) {
    setProcessing(true)

    const reviewAnswersAttributes: Record<string, Record<string, unknown>> = {}
    let idx = 0
    Object.values(answers).forEach((a) => {
      if (!a.answer_value && !a.id) return
      const attrs: Record<string, unknown> = {
        review_question_template_id: a.review_question_template_id,
        answer_value: a.answer_value,
      }
      if (a.id) attrs.id = a.id
      reviewAnswersAttributes[String(idx)] = attrs
      idx++
    })

    router.patch(
      `/performance_review_cycles/${cycle.id}/performance_reviews/${review.id}`,
      {
        performance_review: {
          review_answers_attributes: reviewAnswersAttributes,
        },
      } as any,
      {
        onFinish: () => setProcessing(false),
        onSuccess: () => onSuccess?.(),
      },
    )
  }

  function renderQuestionInput(question: Question) {
    const currentAnswer = answers[question.id]?.answer_value || ""

    switch (question.question_type) {
      case "rating_1_5":
        return (
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((val) => (
              <Button
                key={val}
                type="button"
                variant={currentAnswer === String(val) ? "default" : "outline"}
                size="sm"
                className="h-9 w-9"
                onClick={() => setAnswer(question.id, String(val))}
              >
                {val}
              </Button>
            ))}
            {currentAnswer && <span className="text-muted-foreground text-sm ml-2">{currentAnswer}/5</span>}
          </div>
        )

      case "rating_1_10":
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
              <Button
                key={val}
                type="button"
                variant={currentAnswer === String(val) ? "default" : "outline"}
                size="sm"
                className="h-8 w-8 text-xs"
                onClick={() => setAnswer(question.id, String(val))}
              >
                {val}
              </Button>
            ))}
            {currentAnswer && <span className="text-muted-foreground text-sm ml-2">{currentAnswer}/10</span>}
          </div>
        )

      case "yes_no":
        return (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={currentAnswer.toLowerCase() === "yes" ? "default" : "outline"}
              size="sm"
              onClick={() => setAnswer(question.id, "yes")}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant={currentAnswer.toLowerCase() === "no" ? "default" : "outline"}
              size="sm"
              onClick={() => setAnswer(question.id, "no")}
            >
              No
            </Button>
          </div>
        )

      case "text":
        return (
          <Textarea
            rows={3}
            value={currentAnswer}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            placeholder="Enter your response..."
          />
        )

      case "multiple_choice":
        return (
          <Select value={currentAnswer} onValueChange={(v) => setAnswer(question.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {question.options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      default:
        return (
          <Input
            value={currentAnswer}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            placeholder="Enter your response..."
          />
        )
    }
  }

  const allAnswered = questions.every((q) => answers[q.id]?.answer_value)

  return (
    <>
      <Head title={`Edit Review - ${cycle.display_name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={`/performance_review_cycles/${cycle.id}`}>{cycle.display_name}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Edit Review</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/performance_review_cycles/${cycle.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold capitalize">{review.reviewer_type} Review</h1>
                <Badge variant="secondary">{review.reviewer_name}</Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                {cycle.user_name} - {cycle.display_name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={processing} onClick={handleSave}>
              {processing ? "Saving..." : "Save Progress"}
            </Button>
            {allAnswered && review.status !== "completed" && (
              <Button size="sm" onClick={handleSubmitReview} disabled={processing}>
                <IconSend className="mr-1 h-4 w-4" />
                Submit Review
              </Button>
            )}
          </div>
        </div>

        <form onSubmit={handleSave}>
          {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
            <Card key={category} className="mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{category}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {categoryQuestions.map((question, idx) => (
                    <div key={question.id}>
                      {idx > 0 && <Separator className="mb-6" />}
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <Label className="text-sm font-medium leading-relaxed">{question.question_text}</Label>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {question.question_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                          </Badge>
                        </div>
                        {renderQuestionInput(question)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </form>
      </div>
    </>
  )
}

PerformanceReviewEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
